import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../app/AuthProvider";
import { useSetPin } from "../api/hooks";
import { Button, ErrorNotice, Field, Input, Notice } from "../components/ui";

/**
 * Pantalla obligatoria cuando un administrador ha generado un PIN temporal.
 * Mientras no se elija uno propio, la API rechaza cualquier otra petición
 * (`pin_change_required`), así que aquí solo se puede cambiar el PIN o salir.
 */
export function ForcePinChangePage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const setPin = useSetPin();
  const [pin, setPinValue] = useState("");
  const [pin2, setPin2] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setLocalError(null);
    if (pin !== pin2) {
      setLocalError("Los dos PIN no coinciden.");
      return;
    }
    setPin.mutate(
      { pin, password },
      {
        // Con el PIN ya definitivo, se vacía la caché para recargarlo todo sin los 403 anteriores.
        onSuccess: () => void queryClient.invalidateQueries(),
      },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-100 px-4 py-10 dark:bg-ink-950">
      <form
        onSubmit={submit}
        className="w-full max-w-[420px] space-y-4 rounded-2xl border border-ink-200 bg-white p-6 shadow-lg dark:border-ink-800 dark:bg-ink-900"
      >
        <h1 className="text-lg font-bold text-ink-900 dark:text-ink-50">Elige tu PIN</h1>
        <Notice tone="warn">
          Has entrado con un PIN temporal. Elige uno propio de 6 dígitos para continuar. Evita fechas y números
          repetidos o en secuencia.
        </Notice>
        <Field label="Nuevo PIN (6 dígitos)" htmlFor="force-pin">
          <Input
            id="force-pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            autoFocus
            maxLength={6}
            required
            value={pin}
            onChange={(event) => setPinValue(event.target.value.replace(/\D/g, ""))}
            className="tracking-widest"
          />
        </Field>
        <Field label="Repite el PIN" htmlFor="force-pin-2">
          <Input
            id="force-pin-2"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            required
            value={pin2}
            onChange={(event) => setPin2(event.target.value.replace(/\D/g, ""))}
            className="tracking-widest"
          />
        </Field>
        <Field label="Tu contraseña" htmlFor="force-pin-password" hint="Para confirmar que eres tú.">
          <Input
            id="force-pin-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        {localError ? <p className="text-xs text-signal-danger dark:text-red-400">{localError}</p> : null}
        {setPin.isError ? <ErrorNotice error={setPin.error} /> : null}
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={setPin.isPending || pin.length !== 6 || !password}>
            {setPin.isPending ? "Guardando…" : "Guardar PIN"}
          </Button>
          <Button onClick={auth.logout}>Cerrar sesión</Button>
        </div>
      </form>
    </div>
  );
}
